/**
 * Säljverktyg (Sales Tool) — Super admin page for looking up companies,
 * viewing their Accurator activity, Syna credit data, and platform-wide
 * procurement statistics. Used internally by the sales team.
 */
import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import AppLayout from '../../components/layout/app-layout/app-layout';
import { Card, Text } from '@tremor/react';
import { BarChart } from '../../components/shared/Charts/BarChart/BarChart';
import SummaryBarList from '../../components/shared/Charts/SummaryBarList/SummaryBarList';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import Selector from '../../components/shared/Selector/Selector';
import BtChecklistSelect from '../bt-checklist-select/bt-checklist-select';
import { setOption } from '../bt-checklist-select/checklist';
import { ServiceSalesTool } from '../../services/service-sales-tool';
import { useTranslation } from 'react-i18next';

import { useHistory } from 'react-router-dom';
import { placeOptions } from '../page-supplier-presentation/PlaceSelector';
import FilteringControl from '../../components/shared/FilteringControl/FilteringControl';
import SupplierCompanyCard, {
  groupSupplierUsers,
} from './supplier-company-card';
import './page-sales-tool.scss';

type TimeRange = 'today' | 'all' | '12m' | '6m' | '3m' | '1m';

const timeRangeKeys: TimeRange[] = ['today', 'all', '12m', '6m', '3m', '1m'];

// One shared "no place filter" array, so ticking places under Hela Sverige refetches nothing.
const NO_PLACE_FILTER: string[] = [];

const getDateRange = (range: TimeRange) => {
  if (range === 'all' || range === 'today') return {};
  const now = new Date();
  const months = parseInt(range);
  const from = new Date(now);
  from.setMonth(from.getMonth() - months);
  return { fromDate: from.toISOString(), toDate: now.toISOString() };
};

const PageSalesTool: FC = () => {
  const [t] = useTranslation('salesTool');
  const history = useHistory();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [orgOptions, setOrgOptions] = useState<{ label: string; value: string }[]>([]);
  const [, setSearchLoading] = useState(false);
  const [companyData, setCompanyData] = useState<any>(null);
  const [supplierStats, setSupplierStats] = useState<any>(null);
  const [platformTotals, setPlatformTotals] = useState<any>(null);
  const [globalAreas, setGlobalAreas] = useState<any[]>([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>([]);
  const [selectedPlaceValues, setSelectedPlaceValues] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  const [combinedCategoryStats, setCombinedCategoryStats] = useState<any[]>([]);
  const [topCategories, setTopCategories] = useState<any[]>([]);
  const [geographyData, setGeographyData] = useState<any>(null);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [trendYear, setTrendYear] = useState<number>(new Date().getFullYear());
  const [supplierUsers, setSupplierUsers] = useState<any[]>([]);
  const [reachData, setReachData] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const areaOptions = useMemo(
    () => globalAreas.map((a: any) => ({ label: a.name, value: a._id })),
    [globalAreas]
  );

  const selectedPlaces = useMemo(() => {
    if (!selectedPlaceValues.length || selectedPlaceValues.includes('Hela Sverige')) return NO_PLACE_FILTER;
    return selectedPlaceValues;
  }, [selectedPlaceValues]);

  // Load superadmin global categories and organization list on mount
  useEffect(() => {
    const loadAreas = async () => {
      try {
        const areas = await ServiceSalesTool.getGlobalCategories();
        setGlobalAreas(areas || []);
      } catch (err) {
        console.error('Failed to load global categories:', err);
      }
    };
    const loadOrgs = async () => {
      try {
        const orgs = await ServiceSalesTool.getOrganizations();
        setOrgOptions((orgs || []).map((org: any) => ({
          label: `${org.name} — ${org.organizationNumber || ''}`,
          value: org.organizationNumber || org._id,
        })));
      } catch (err) {
        console.error('Failed to load organizations:', err);
      }
    };
    loadAreas();
    loadOrgs();
    loadPlatformTotals();
    loadGeography();
    loadTopCategories();
  }, []);

  const loadPlatformTotals = async () => {
    try {
      const totals = await ServiceSalesTool.getPlatformTotals();
      setPlatformTotals(totals);
    } catch (err) {
      console.error('Failed to load platform totals:', err);
    }
  };

  const loadGeography = async () => {
    try {
      const geo = await ServiceSalesTool.getGeography();
      setGeographyData(geo);
    } catch (err) {
      console.error('Failed to load geography:', err);
    }
  };

  const loadTopCategories = async () => {
    try {
      const top = await ServiceSalesTool.getTopCategories({ limit: 200 });
      setTopCategories(top);
    } catch (err) {
      console.error('Failed to load top categories:', err);
    }
  };

  // Search handler — accepts query directly to avoid stale closure issues
  const handleSearch = useCallback(async (query?: string) => {
    const q = (query || searchQuery).trim();
    if (!q) return;
    setSearchLoading(true);
    setCompanyData(null);
    setSupplierStats(null);

    try {
      // Syna lookup uses raw query; supplier stats needs normalized org number (no dashes/spaces)
      const [company, stats] = await Promise.all([
        ServiceSalesTool.companyLookup(q).catch(() => null),
        ServiceSalesTool.getSupplierStats(q.replace(/[-\s]/g, '')).catch(() => null),
      ]);
      setCompanyData(company);
      setSupplierStats(stats);

      if (stats?.organizationNumber) {
        loadPrioData(stats.organizationNumber, stats.categories || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const loadPrioData = async (orgNum: string, areaIds: string[]) => {
    try {
      const [users, reach] = await Promise.all([
        ServiceSalesTool.getSupplierUsers(orgNum).catch(() => []),
        ServiceSalesTool.getSupplierReach(orgNum, areaIds).catch(() => null),
      ]);
      setSupplierUsers(users);
      setReachData(reach);
    } catch (err) {
      console.error('Failed to load prio data:', err);
    }
  };

  // Load combined category stats when filters change
  useEffect(() => {
    if (selectedAreaIds.length === 0) {
      setCombinedCategoryStats([]);
      setTrendData([]);
      setStatsLoading(false);
      return undefined;
    }
    let stale = false;
    setStatsLoading(true);
    const dateRange = getDateRange(timeRange);
    const places = selectedPlaces.length > 0 ? selectedPlaces : undefined;

    Promise.all([
      ServiceSalesTool.getCategoryStatsCombined(selectedAreaIds, {
        places,
        ...dateRange,
        ongoingOnly: timeRange === 'today',
      }),
      ServiceSalesTool.getTrend({
        areaIds: selectedAreaIds,
        period: 'monthly',
        places,
      }),
    ])
      .then(([cats, trend]) => {
        if (stale) return;
        setCombinedCategoryStats(cats);
        setTrendData(trend);
        // Default to latest year with data
        if (trend.length > 0) {
          const maxYear = Math.max(...trend.map((d: any) => d.year));
          setTrendYear(maxYear);
        }
      })
      .catch(err => console.error('Failed to load category stats:', err))
      .finally(() => {
        if (!stale) setStatsLoading(false);
      });
    // Only the newest request may fill the charts, whichever request answers first.
    return () => { stale = true; };
  }, [selectedAreaIds, selectedPlaces, timeRange]);

  // Memoized user grouping by email
  const groupedUsers = useMemo(() => groupSupplierUsers(supplierUsers), [supplierUsers]);

  // companyData is the server-parsed lookup response ({ companyInfo, ratingInfo, kpiData }).
  const companyInfo = useMemo(() => companyData?.companyInfo ?? null, [companyData]);
  const ratingInfo = useMemo(() => companyData?.ratingInfo ?? null, [companyData]);
  const kpiData = useMemo(() => companyData?.kpiData ?? [], [companyData]);

  return (
    <AppLayout title={t('title', 'Säljverktyg')}>
      <div className="sales-tool">
        {/* Search Section — creatable dropdown with all org accounts + free text */}
        <div className="sales-tool__search-section">
          <div className="sales-tool__search-input">
            <Selector
              isCreatable
              isClearable
              isSearchable
              placeholder={t('search.placeholder', 'Sök på organisationsnummer eller företagsnamn...')}
              options={orgOptions}
              value={selectedOrg}
              formatCreateLabel={(input: string) => t('search.formatCreateLabel', 'Sök "{{input}}"...', { input })}
              onChange={(option: any) => {
                setSelectedOrg(option);
                if (!option) {
                  // Cleared
                  setCompanyData(null);
                  setSupplierStats(null);
                  setSearchQuery('');
                  return;
                }
                const q = option.value;
                setSearchQuery(q);
                handleSearch(q);
              }}
            />
          </div>
        </div>

        {/* === Allmän information om Accurator === */}
        <div className="sales-tool__section">
          <h3 className="sales-tool__section-title">{t('section.generalInfo', 'Allmän information om Accurator')}</h3>
          <div className="sales-tool__platform-grid">
            {[
              { label: t('platform.purchasingCustomers.label', 'Inköpskunder (totalt)'), value: platformTotals?.purchasingCustomers || 0,
                tooltip: t('platform.purchasingCustomers.tooltip', 'Antal aktiva organisationer med inköpskonto (activeProducts innehåller PURCHASING).') },
              { label: t('platform.publicTenderCustomers.label', 'Offentliga upphandlingar (tillägg)'), value: platformTotals?.publicTenderCustomers || 0,
                tooltip: t('platform.publicTenderCustomers.tooltip', 'Antal aktiva inköpsorganisationer med tillägget Offentliga upphandlingar. De ingår redan i Inköpskunder.') },
              { label: t('platform.salesCustomers.label', 'Leverantörskunder (totalt)'), value: platformTotals?.salesCustomers || 0,
                tooltip: t('platform.salesCustomers.tooltip', 'Antal aktiva organisationer med säljkonto (activeProducts innehåller SALES).') },
              { label: t('platform.prioSubscriptions.label', 'Prio-medlemskap (aktiva)'), value: platformTotals?.prioSubscriptions || 0,
                tooltip: t('platform.prioSubscriptions.tooltip', 'Antal aktiva Accurator Prio-prenumerationer (produkt RECOMMENDED med giltig slutdatum).') },
              { label: t('platform.publishedSubscriptions.label', 'Publicerade förfrågningar (aktiva abonnemang)'), value: platformTotals?.publishedSubscriptions || 0,
                tooltip: t('platform.publishedSubscriptions.tooltip', 'Antal aktiva prenumerationer på Publicerade förfrågningar (produkt OPEN_QUOTATIONS med giltig slutdatum).') },
              { label: t('platform.ongoingProcurements.label', 'Pågående upphandlingar'), value: platformTotals?.ongoingProcurements || 0,
                tooltip: t('platform.ongoingProcurements.tooltip', 'Antal inköp (areas) i öppna projekt (isOpen: true) där anbudstiden inte gått ut.') },
              { label: t('platform.totalProcurements.label', 'Upphandlingar (totalt)'), value: platformTotals?.totalProcurements || 0,
                tooltip: t('platform.totalProcurements.tooltip', 'Totalt antal inköp (project areas) i aktiva, ej borttagna projekt på hela plattformen.') },
              { label: t('platform.totalVolume.label', 'Total upphandlad volym'), value: platformTotals?.totalVolume || 0, isMSEK: true,
                tooltip: t('platform.totalVolume.tooltip', 'Summan av alla signerade kontraktsvärden (Scrive-kontrakt med signedAt + manuella kontrakt) i aktiva projekt.') },
              { label: t('platform.totalUniqueSuppliers.label', 'Unika leverantörer (totalt)'), value: platformTotals?.totalUniqueSuppliers || 0,
                tooltip: t('platform.totalUniqueSuppliers.tooltip', 'Antal unika organisationsnummer bland alla aktiva leverantörer på plattformen (exkl. superadmin-organisationer).') },
            ].map((item: any) => (
              <TooltipContainer
                key={item.label}
                renderReferenceComponent={(className, ref) => (
                  <div className={`sales-tool__platform-tile ${className}`} ref={ref}>
                    <div className="sales-tool__platform-tile-value">
                      {item.isMSEK ? (
                        <>{Math.round(item.value / 1000000).toLocaleString('sv-SE')} <span className="sales-tool__platform-tile-unit">{t('unit.MSEK', 'MSEK')}</span></>
                      ) : (
                        typeof item.value === 'number' ? item.value.toLocaleString('sv-SE') : item.value
                      )}
                    </div>
                    <div className="sales-tool__platform-tile-label">{item.label}</div>
                  </div>
                )}
              >
                <div style={{ maxWidth: 280, fontSize: 13, color: 'var(--gray-700)', padding: '4px 0' }}>
                  <strong style={{ display: 'block', marginBottom: 4 }}>{item.label}</strong>
                  {item.tooltip}
                </div>
              </TooltipContainer>
            ))}
          </div>
        </div>

        {/* Company Info Card */}
        <SupplierCompanyCard
          companyInfo={companyInfo}
          supplierStats={supplierStats}
          ratingInfo={ratingInfo}
          kpiData={kpiData}
          groupedUsers={groupedUsers}
          reachData={reachData}
          history={history}
        />

        <div className="sales-tool__section">
          <h3 className="sales-tool__section-title">{t('section.procurementsPerCategory', 'Upphandlingar per kategori')}</h3>

          <div className="selector-row">
            <div>
              <BtChecklistSelect
                placeholder={t('filter.categories', 'Kategorier')}
                options={areaOptions}
                value={selectedAreaIds}
                onChange={setSelectedAreaIds}
              />
              {supplierStats?.supplierProfile?.areas?.length > 0 && (() => {
                // Map supplier's org-specific area IDs to global categories via customerAreas
                const supplierGlobalMatches = supplierStats.supplierProfile.areas
                  .map((area: any) => {
                    if (!area?._id) return null;
                    const globalMatch = globalAreas.find((g: any) => g._id === area._id);
                    return globalMatch ? { globalId: globalMatch._id, globalName: globalMatch.name } : null;
                  })
                  .filter(Boolean)
                  // Deduplicate by global ID
                  .filter((v: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.globalId === v.globalId) === i);

                if (!supplierGlobalMatches.length) return null;
                return (
                  <div className="sales-tool__profile-tags">
                    <span className="sales-tool__profile-tags-label">{t('filter.supplierCategories', 'Leverantörens:')}</span>
                    {supplierGlobalMatches.map((match: any) => {
                      const isSelected = selectedAreaIds.includes(match.globalId);
                      return (
                        <button
                          key={match.globalId}
                          className={`sales-tool__profile-tag ${isSelected ? 'sales-tool__profile-tag--active' : ''}`}
                          onClick={() => setSelectedAreaIds(setOption(selectedAreaIds, match.globalId, !isSelected))}
                        >
                          {match.globalName}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div>
              <BtChecklistSelect
                placeholder={t('filter.operatingArea', 'Verksamhetsområde')}
                options={placeOptions}
                value={selectedPlaceValues}
                onChange={setSelectedPlaceValues}
              />
              {supplierStats?.supplierProfile?.places?.length > 0 && (
                <div className="sales-tool__profile-tags">
                  <span className="sales-tool__profile-tags-label">{t('filter.supplierCategories', 'Leverantörens:')}</span>
                  {supplierStats.supplierProfile.places.map((place: string) => {
                    const option = placeOptions.find((o: any) => o.value === place) || { label: place, value: place };
                    const isSelected = selectedPlaceValues.includes(place);
                    return (
                      <button
                        key={place}
                        className={`sales-tool__profile-tag ${isSelected ? 'sales-tool__profile-tag--active' : ''}`}
                        onClick={() => setSelectedPlaceValues(setOption(selectedPlaceValues, place, !isSelected))}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="sales-tool__filters">
            <div className="sales-tool__filters-group">
              <label>{t('filter.timePeriod', 'Tidsperiod')}</label>
              <FilteringControl
                options={timeRangeKeys.map(key => ({ value: key, label: t(`timeRange.${key}`, key) }))}
                value={timeRange}
                onChange={v => setTimeRange(v as TimeRange)}
              />
            </div>
          </div>

          {/* Combined Category Stats Chart */}
          {statsLoading && <div className="sales-tool__loading">{t('loading.stats', 'Laddar statistik...')}</div>}
          {!statsLoading && selectedAreaIds.length === 0 && (
            <Card style={{ padding: '48px 32px', textAlign: 'center', border: '1px dashed var(--gray-300)', boxShadow: 'none' }}>
              <Text style={{ color: 'var(--gray-400)', fontSize: 14 }}>{t('empty.selectCategory', 'Välj kategori och verksamhetsområde för att visa data')}</Text>
            </Card>
          )}
          {!statsLoading && selectedAreaIds.length > 0 && combinedCategoryStats.length === 0 && (
            <Card style={{ padding: '32px', textAlign: 'center' }}>
              <Text style={{ color: 'var(--gray-500)' }}>{t('empty.noDataForCategory', 'Ingen data för vald kategori')}</Text>
            </Card>
          )}
          {!statsLoading && combinedCategoryStats.length > 0 && (
            <BarChart
              title={t('chart.procurementsPerCategory', 'Upphandlingar per kategori (publicerade vs ej publicerade)')}
              index="areaName"
              data={combinedCategoryStats.map(c => ({
                areaName: c.areaName || t('chart.unknown', 'Okänd'),
                [t('chart.published', 'Publicerade')]: c.publicCount || 0,
                [t('chart.notPublished', 'Ej publicerade')]: c.privateCount || 0,
              }))}
              categories={[t('chart.published', 'Publicerade'), t('chart.notPublished', 'Ej publicerade')]}
              colors={['orange', 'cyan']}
            />
          )}

          {/* Trend — inside Upphandlingar per kategori section */}
          {trendData.length > 0 && (() => {
            const years = Array.from(new Set(trendData.map(d => d.year).filter(Boolean))).sort((a: number, b: number) => b - a);
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
            const filtered = trendData.filter(d => d.year === trendYear);
            return (
              <div style={{ marginTop: 16 }}>
                <div className="sales-tool__section-header" style={{ marginBottom: 8 }}>
                  <div />
                  <FilteringControl
                    options={years.map(y => ({ value: String(y), label: String(y) }))}
                    value={String(trendYear)}
                    onChange={y => setTrendYear(Number(y))}
                  />
                </div>
                <BarChart
                  title={t('chart.procurementsOverTime', 'Upphandlingar över tid (för valda kategorier)')}
                  index="period"
                  data={monthNames.map((name, i) => {
                    const match = filtered.find(d => d.month === i + 1);
                    return { period: name, [t('chart.count', 'Antal')]: match?.count || 0 };
                  })}
                  categories={[t('chart.count', 'Antal')]}
                  colors={['orange']}
                />
              </div>
            );
          })()}
        </div>

        {/* Top Categories & Geography */}
        <div className="sales-tool__section">
          <div className="sales-tool__chart-grid">
            <SummaryBarList
              title={t('chart.topCategories.title', 'Mest efterfrågade kategorier')}
              infoText={t('chart.topCategories.info', 'Visar de kategorier som har flest upphandlingar på plattformen. Kategorier i orange är globala (superadmin), gråa kategorier saknar koppling till en global kategori.')}
              data={topCategories.map(c => {
                const isGlobal = globalAreas.some((g: any) => g._id === c.areaId);
                return {
                  name: c.areaName || t('chart.unknown', 'Okänd'),
                  value: c.count,
                  ...(!isGlobal ? { color: 'gray' as const } : {}),
                };
              })}
              keyColumnName={t('chart.topCategories.keyColumn', 'Kategori')}
              valueColumName={t('chart.topCategories.valueColumn', 'Antal')}
              maxItems={10}
            />
            {geographyData && (
              <SummaryBarList
                title={t('chart.customersByRegion.title', 'Accurator kunder per region')}
                infoText={t('chart.customersByRegion.info', 'Antal kundorganisationer med inköpskonto per region. Baserat på organisationens registrerade kommun.')}
                data={(geographyData.byRegion || []).map((r: any) => ({
                  name: r.region,
                  value: r.count,
                }))}
                keyColumnName={t('chart.customersByRegion.keyColumn', 'Region')}
                valueColumName={t('chart.customersByRegion.valueColumn', 'Antal kunder')}
                maxItems={10}
              />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default PageSalesTool;
